export interface MemberSheetRow {
    id: number
    name?: string
    phone?: string
    birthday?: string
    balance?: string | number
    password?: string
    points?: string | number
    cardCode?: string
}

interface MemberSheetColumn {
    title: string
    dataIndex: keyof Omit<MemberSheetRow, 'id'>
    aliases?: string[]
}

export const sheetColumns: MemberSheetColumn[] = [
    { title: '会员姓名', dataIndex: 'name' },
    { title: '手机号', dataIndex: 'phone' },
    { title: '生日', dataIndex: 'birthday' },
    { title: '余额', dataIndex: 'balance' },
    { title: '会员密码', dataIndex: 'password', aliases: ['密码'] },
    { title: '积分', dataIndex: 'points' },
    { title: '会员卡号', dataIndex: 'cardCode' },
]

export const requiredColumns = sheetColumns.map(column => column.title)

// 后端接收时仍需要固定的 A-G 列；上传表格本身只要求老版约定的三个必填字段。
export const requiredInputColumns = ['会员姓名', '手机号', '余额']

const headerIncludes = (header: unknown, title: string) => String(header ?? '').includes(title)

export const getColumnIndex = (header: string[], column: MemberSheetColumn) =>
    header.findIndex(item => [column.title, ...(column.aliases || [])].some(title => headerIncludes(item, title)))

export const getMissingColumns = (header: string[]) =>
    requiredInputColumns.filter(title => !header.some(item => headerIncludes(item, title)))

export const getPreviewRows = (rows: any[][]): MemberSheetRow[] => {
    const header = rows[0] || []
    const columnIndexMap = sheetColumns.map(column => getColumnIndex(header, column))

    return rows
        .slice(1)
        .filter(row => row.some(cell => cell !== undefined && cell !== null && cell !== ''))
        .map((row, index) =>
            sheetColumns.reduce<MemberSheetRow>(
                (result, column, columnIndex) => ({
                    ...result,
                    [column.dataIndex]: row[columnIndexMap[columnIndex]],
                }),
                { id: index },
            ),
        )
}

export const getCanonicalSheetRows = (previewRows: MemberSheetRow[]) => [
    requiredColumns,
    ...previewRows.map(row => sheetColumns.map(column => row[column.dataIndex] ?? '')),
]

export const isValidDate = (value: string) => {
    if (!/^\d{8}$/.test(value)) return false
    const year = Number(value.slice(0, 4))
    const month = Number(value.slice(4, 6))
    const day = Number(value.slice(6, 8))
    const date = new Date(year, month - 1, day)

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
}

export const validateMemberSheetRow = (row: MemberSheetRow, rowNumber: number) => {
    const name = row.name === undefined || row.name === null ? '' : String(row.name).trim()
    const phone = row.phone === undefined || row.phone === null ? '' : String(row.phone).trim()
    const birthday = row.birthday === undefined || row.birthday === null ? '' : String(row.birthday).trim()
    const balance = row.balance === undefined || row.balance === null ? '' : String(row.balance).trim()
    const password = row.password === undefined || row.password === null ? '' : String(row.password).trim()
    const points = row.points === undefined || row.points === null ? '' : String(row.points).trim()
    const cardCode = row.cardCode === undefined || row.cardCode === null ? '' : String(row.cardCode).trim()
    const prefix = `第 ${rowNumber} 行`

    if (!name) return `${prefix}：会员姓名为必填项`
    if (name.length > 13) return `${prefix}：会员姓名最长 13 位`
    if (!phone) return `${prefix}：手机号为必填项`
    if (!/^\d{11}$/.test(phone)) return `${prefix}：手机号必须是 11 位数字`
    if (birthday && !isValidDate(birthday)) return `${prefix}：生日格式应为 YYYYMMDD，例如 19900101`
    if (!balance) return `${prefix}：余额为必填项`
    if (!/^\d+(\.\d{1,2})?$/.test(balance) || Number(balance) > 2147483647) {
        return `${prefix}：余额应为不超过两位小数的有效金额`
    }
    if (password && !/^\d{6}$/.test(password)) return `${prefix}：会员密码必须是 6 位数字`
    if (points && (Number.isNaN(Number(points)) || Number(points) < 0)) return `${prefix}：积分必须是大于等于 0 的数字`
    if (cardCode && !/^\d{4,12}$/.test(cardCode)) return `${prefix}：会员卡号必须是 4 到 12 位数字`

    return ''
}

export const validateMemberSheetRows = (rows: MemberSheetRow[]) => {
    const phoneRowMap = new Map<string, number>()

    for (let index = 0; index < rows.length; index++) {
        const rowNumber = index + 2
        const rowMessage = validateMemberSheetRow(rows[index], rowNumber)
        if (rowMessage) return rowMessage

        const phone =
            rows[index].phone === undefined || rows[index].phone === null ? '' : String(rows[index].phone).trim()
        const existedRowNumber = phoneRowMap.get(phone)
        if (existedRowNumber) return `第 ${rowNumber} 行：手机号与第 ${existedRowNumber} 行重复`

        phoneRowMap.set(phone, rowNumber)
    }

    return ''
}

export const partitionMemberSheetRows = (rows: MemberSheetRow[]) => {
    const validRows: MemberSheetRow[] = []
    const invalidRows: Array<{ row: MemberSheetRow; reason: string }> = []
    const phoneRowMap = new Map<string, number>()

    rows.forEach((row, index) => {
        const rowNumber = index + 2
        const phone = row.phone === undefined || row.phone === null ? '' : String(row.phone).trim()
        const existedRowNumber = phoneRowMap.get(phone)
        const reason =
            validateMemberSheetRow(row, rowNumber) ||
            (existedRowNumber ? `第 ${rowNumber} 行：手机号与第 ${existedRowNumber} 行重复` : '')

        if (reason) {
            invalidRows.push({ row, reason })
        } else {
            phoneRowMap.set(phone, rowNumber)
            validRows.push(row)
        }
    })

    return { validRows, invalidRows }
}
